import type { CSSProperties, KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { WeeklySlot } from '@/lib/publishQueues/types';
import type { CalendarEdit } from '@/lib/publishQueues/editCalendarSlot';
import { weeklySlotTimeLabel } from '@/lib/publishQueues/weeklySlots';
import { cn } from '@/lib/utils';

type Props = {
  index: number;
  slot: WeeklySlot;
  dayLabel: string;
  style: CSSProperties;
  editing: boolean;
  onPointerStart: (event: PointerEvent<HTMLElement>, mode: CalendarEdit, index: number, slot: WeeklySlot) => void;
  onKeyboardEdit: (mode: CalendarEdit, event: KeyboardEvent<HTMLButtonElement>) => void;
  onSelect: (edge?: 'start' | 'end') => void;
  consumeClick: (event: MouseEvent) => boolean;
  /** Omitted when the slot cannot be removed (e.g. it is the queue's last one). */
  onRemove?: () => void;
};

export function CalendarSlotBlock({ index, slot, dayLabel, style, editing, onPointerStart, onKeyboardEdit, onSelect, consumeClick, onRemove }: Props) {
  const { t } = useTranslation();
  const label = `${dayLabel} ${weeklySlotTimeLabel(slot)}`;
  return (
    <div data-slot-index={index} className={cn('group/slot absolute rounded text-primary hover:z-10 focus-within:z-10', editing && 'opacity-30')} style={style}>
      <button type="button" title={label} aria-label={label} aria-describedby="queue-calendar-help"
        data-slot-body={index}
        onPointerDown={(event) => onPointerStart(event, 'move', index, slot)}
        onKeyDown={(event) => {
          if (onRemove && (event.key === 'Delete' || event.key === 'Backspace')) {
            event.preventDefault();
            onRemove();
            return;
          }
          onKeyboardEdit('move', event);
        }}
        onClick={(event) => { if (!consumeClick(event)) onSelect(); }}
        className="absolute inset-0 flex touch-none cursor-grab flex-col justify-center overflow-hidden rounded border border-primary/40 bg-primary/15 px-1 text-left text-[10px] leading-tight tabular-nums transition-colors hover:bg-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:cursor-grabbing">
        <span className="block w-full truncate font-semibold">{slot.timeLocal}</span>
        {Number(style.height) >= 44 && <span className="block w-full truncate opacity-75">{slot.endTimeLocal}</span>}
      </button>
      {(['start', 'end'] as const).map((edge) => (
        <button key={edge} type="button" data-resize={edge}
          title={t(edge === 'start' ? 'publishQueues.form.resizeStart' : 'publishQueues.form.resizeEnd', { range: label })}
          aria-label={t(edge === 'start' ? 'publishQueues.form.resizeStart' : 'publishQueues.form.resizeEnd', { range: label })}
          onPointerDown={(event) => onPointerStart(event, edge, index, slot)}
          onKeyDown={(event) => onKeyboardEdit(edge, event)}
          onClick={(event) => { if (!consumeClick(event)) onSelect(edge); }}
          className={cn('absolute inset-x-0 flex h-2.5 touch-none cursor-ns-resize items-center justify-center rounded hover:bg-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            edge === 'start' ? 'top-0' : 'bottom-0')}>
          <span aria-hidden="true" className="h-0.5 w-2/5 max-w-5 rounded bg-primary/60" />
        </button>
      ))}
      {onRemove && !editing && (
        <button type="button" data-slot-delete={index}
          title={t('publishQueues.form.deleteSlot', { range: label })}
          aria-label={t('publishQueues.form.deleteSlot', { range: label })}
          // Keep the column/block drag gestures from starting underneath the button.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.stopPropagation(); onRemove(); }}
          className="absolute -right-2 -top-2 z-20 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity hover:border-destructive hover:bg-destructive hover:text-destructive-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive group-hover/slot:opacity-100 group-focus-within/slot:opacity-100 [@media(hover:none)]:opacity-100">
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
